import { DialogComponent } from '@theme/dialog';

const DEFAULT_EXAMPLE_IDEAS = ['A + J', '12.03.24', 'Love you', 'Mum', 'Forever'];

/**
 * @param {string} value
 */
function sanitiseValue(value) {
  return (value || '').replace(/\s+/g, ' ').trim();
}

/**
 * @param {number} maxChars
 */
function emptyPreviewSampleText(maxChars) {
  const n = Math.max(0, Number(maxChars) || 3);
  return 'ABC'.slice(0, n);
}

/**
 * @typedef {object} EngravingPreviewConfig
 * @property {number} [x]
 * @property {number} [y]
 * @property {number} [width]
 * @property {number} [height]
 * @property {number} [rotation]
 * @property {string} [fontSize]
 * @property {string} [mode]
 * @property {number} [minScale]
 * @property {number} [scaleStep]
 */

/**
 * @typedef {object} EngravingConfig
 * @property {number} [maxChars]
 * @property {number} [fee]
 * @property {string} [feeVariantId]
 * @property {string} [helperText]
 * @property {string} [policyText]
 * @property {string} [introText]
 * @property {string} [previewNote]
 * @property {string} [drawerTitle]
 * @property {string} [examplesLabel]
 * @property {string} [inputPlaceholder]
 * @property {string} [ctaTitle]
 * @property {string} [ctaSubtext]
 * @property {string[]} [exampleIdeas]
 * @property {EngravingPreviewConfig} [preview]
 */

/**
 * @typedef {object} ProductEngravingCtaRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLScriptElement} configScript
 * @property {HTMLInputElement} hiddenInput
 * @property {HTMLInputElement} [feeVariantInput]
 * @property {HTMLInputElement} [feeQuantityInput]
 * @property {HTMLElement} ctaCard
 * @property {HTMLElement} summary
 * @property {HTMLElement} summaryText
 * @property {HTMLElement} summaryFee
 * @property {HTMLButtonElement} editButton
 * @property {HTMLButtonElement} removeButton
 * @property {HTMLElement} buttonPrice
 * @property {HTMLElement} toggleLabel
 * @property {HTMLInputElement} panelInput
 * @property {HTMLElement} characterCount
 * @property {HTMLElement} previewText
 * @property {HTMLElement} previewNote
 * @property {HTMLElement} helper
 * @property {HTMLElement} policyEl
 * @property {HTMLButtonElement} saveButton
 * @property {HTMLElement} drawerTitle
 * @property {HTMLElement} drawerSubtitle
 * @property {HTMLElement} examplesLabel
 * @property {HTMLElement} exampleChips
 * @property {HTMLElement} ctaTitle
 * @property {HTMLElement} ctaHintPrimary
 * @property {HTMLElement} ctaHintSecondary
 * @property {HTMLElement} marketingLead
 * @property {HTMLElement} marketingCopy
 */

/**
 * @extends {DialogComponent<ProductEngravingCtaRefs>}
 */
class ProductEngravingCta extends DialogComponent {
  /** @type {EngravingConfig | null} */
  #config = null;

  connectedCallback() {
    super.connectedCallback();
    this.#config = this.#parseConfig();
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('change', this.#handleInput);
    this.addEventListener('keyup', this.#handleInput);
    this.addEventListener('paste', this.#handleInput);
    this.addEventListener('click', this.#handleClick);
    this.#updateUI();
    this.clearEngraving();
  }

  showDialog() {
    const saved = this.refs.hiddenInput?.value || '';

    if (this.refs.panelInput) {
      this.refs.panelInput.value = saved;
    }

    this.#syncPreview();
    super.showDialog();

    requestAnimationFrame(() => {
      this.refs.panelInput?.focus();
      if (saved) {
        this.refs.panelInput?.select();
      }
    });
  }

  saveEngraving() {
    const config = this.#getConfig();
    const value = sanitiseValue(this.refs.panelInput?.value || '');

    if (!value || !config) return;

    if (this.refs.panelInput) {
      this.refs.panelInput.value = value;
    }

    this.#upsertHiddenInput('properties[Engraving]', value, this.refs.hiddenInput);

    const feeVariantId = String(config.feeVariantId || '').trim();
    if (feeVariantId) {
      this.#upsertHiddenInput('items[1][id]', feeVariantId, this.refs.feeVariantInput);
      this.#upsertHiddenInput('items[1][quantity]', '1', this.refs.feeQuantityInput);
    } else {
      this.#removeHiddenInput(this.refs.feeVariantInput);
      this.#removeHiddenInput(this.refs.feeQuantityInput);
    }

    this.#showSummary(value);
    this.closeDialog();
  }

  clearEngraving() {
    if (this.refs.panelInput) {
      this.refs.panelInput.value = '';
    }

    this.#removeHiddenInput(this.refs.hiddenInput);
    this.#removeHiddenInput(this.refs.feeVariantInput);
    this.#removeHiddenInput(this.refs.feeQuantityInput);
    this.#hideSummary();
    this.#syncPreview();
  }

  cancelEngraving() {
    this.#resetPanelToSaved();
    this.closeDialog();
  }

  /**
   * @param {string} name
   * @param {string} value
   * @param {HTMLInputElement | undefined} existingInput
   */
  #upsertHiddenInput(name, value, existingInput) {
    const input = existingInput;
    if (!input) return;

    input.name = name;
    input.value = value;
    input.disabled = false;
  }

  /**
   * @param {HTMLInputElement | undefined} input
   */
  #removeHiddenInput(input) {
    if (!input) return;
    input.name = '';
    input.value = '';
    input.disabled = true;
  }

  #resetPanelToSaved() {
    const saved = this.refs.hiddenInput?.value || '';

    if (this.refs.panelInput) {
      this.refs.panelInput.value = saved;
    }

    this.#syncPreview();
  }

  /**
   * @param {string} value
   */
  #showSummary(value) {
    const config = this.#getConfig();
    const fee = Number(config?.fee || 15);

    if (this.refs.summaryText) {
      this.refs.summaryText.textContent = value;
    }

    if (this.refs.summaryFee) {
      this.refs.summaryFee.textContent = `+${this.#formatMoney(fee)}`;
    }

    if (this.refs.toggleLabel) {
      const addLabel = this.dataset.addLabel || 'Add engraving';
      const editLabel = this.dataset.editLabel || 'Edit engraving';
      this.refs.toggleLabel.textContent = value ? editLabel : addLabel;
    }

    if (this.refs.summary) {
      this.refs.summary.hidden = false;
    }

    if (this.refs.ctaCard) {
      this.refs.ctaCard.hidden = true;
    }
  }

  #hideSummary() {
    if (this.refs.summary) {
      this.refs.summary.hidden = true;
    }

    if (this.refs.ctaCard) {
      this.refs.ctaCard.hidden = false;
    }
  }

  #parseConfig() {
    const configEl = this.refs.configScript;
    if (!configEl?.textContent) return null;

    try {
      const parsed = JSON.parse(configEl.textContent);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  #getConfig() {
    if (!this.#config) {
      this.#config = this.#parseConfig();
    }
    return this.#config;
  }

  #updateUI() {
    const config = this.#getConfig();
    if (!config) return;

    const maxChars = Number(config.maxChars || 3);
    const fee = Number(config.fee || 15);
    const preview = config.preview || {};
    const previewX = preview.x != null ? preview.x : 26;
    const previewY = preview.y != null ? preview.y : 45;
    const previewWidth = preview.width != null ? preview.width : 18;
    const previewHeight = preview.height != null ? preview.height : 10;
    const previewMode = preview.mode || 'classic';
    const baseFontSize = preview.fontSize || 'clamp(10px, 1.8vw, 24px)';
    const helperText =
      config.helperText ||
      `Max ${maxChars} character${maxChars === 1 ? '' : 's'}.`;
    const previewNoteText = config.previewNote || 'Preview guide only.';
    const examplesLabelText =
      config.examplesLabel != null ? String(config.examplesLabel) : this.refs.examplesLabel?.textContent || '';
    const priceLine = `+${this.#formatMoney(fee)}`;

    if (this.refs.panelInput) {
      this.refs.panelInput.maxLength = maxChars;
      if (config.inputPlaceholder) {
        this.refs.panelInput.placeholder = String(config.inputPlaceholder);
      }
    }

    if (this.refs.helper) this.refs.helper.textContent = helperText;
    if (this.refs.previewNote) this.refs.previewNote.textContent = previewNoteText;
    if (this.refs.buttonPrice) this.refs.buttonPrice.textContent = priceLine;
    if (this.refs.examplesLabel) this.refs.examplesLabel.textContent = examplesLabelText;

    if (config.drawerTitle && this.refs.drawerTitle) {
      this.refs.drawerTitle.textContent = String(config.drawerTitle);
    }

    if (config.drawerSubtitle && this.refs.drawerSubtitle) {
      this.refs.drawerSubtitle.textContent = String(config.drawerSubtitle);
    }

    if (config.marketingLead && this.refs.marketingLead) {
      this.refs.marketingLead.textContent = String(config.marketingLead);
    }

    if (config.marketingCopy && this.refs.marketingCopy) {
      this.refs.marketingCopy.textContent = String(config.marketingCopy);
    }

    if (config.ctaTitle && this.refs.ctaTitle) {
      this.refs.ctaTitle.textContent = String(config.ctaTitle);
    }

    if (config.ctaSubtext && this.refs.ctaHintPrimary) {
      this.refs.ctaHintPrimary.textContent = String(config.ctaSubtext);
    }

    if (config.ctaHintSecondary && this.refs.ctaHintSecondary) {
      this.refs.ctaHintSecondary.textContent = String(config.ctaHintSecondary);
    }

    if (this.refs.previewText) {
      this.refs.previewText.style.left = `${previewX}%`;
      this.refs.previewText.style.top = `${previewY}%`;
      this.refs.previewText.style.width = `${previewWidth}%`;
      this.refs.previewText.style.height = `${previewHeight}%`;
      this.refs.previewText.style.fontSize = baseFontSize;
      this.#applyPreviewMode(this.refs.previewText, previewMode);
    }

    this.#renderExampleChips(config);
    this.#syncPreview();
  }

  /**
   * @param {HTMLElement} previewText
   * @param {string} mode
   */
  #applyPreviewMode(previewText, mode) {
    if (mode === 'soft') {
      previewText.style.fontWeight = '500';
      previewText.style.letterSpacing = '0';
      previewText.style.color = 'rgba(241, 233, 210, 0.8)';
      previewText.style.opacity = '0.9';
      previewText.style.textShadow = '0 0 0.5px rgba(255,255,255,0.35), 0 0 0.8px rgba(80,60,20,0.08)';
      previewText.style.textTransform = 'none';
      previewText.style.fontStyle = 'italic';
      return;
    }

    previewText.style.fontWeight = '600';
    previewText.style.letterSpacing = '-0.01em';
    previewText.style.color = 'rgba(88, 70, 30, 0.9)';
    previewText.style.opacity = '0.95';
    previewText.style.textShadow = 'none';
    previewText.style.textTransform = 'none';
    previewText.style.fontStyle = 'normal';
  }

  /**
   * @param {EngravingConfig} config
   */
  #renderExampleChips(config) {
    const chipsContainer = this.refs.exampleChips;
    if (!chipsContainer) return;

    const maxChars = Number(config.maxChars || 3);
    let ideas = DEFAULT_EXAMPLE_IDEAS;

    if (Array.isArray(config.exampleIdeas) && config.exampleIdeas.length) {
      ideas = config.exampleIdeas.map((item) => String(item));
    }

    chipsContainer.textContent = '';

    ideas.forEach((idea) => {
      const trimmed = sanitiseValue(idea).slice(0, maxChars);
      if (!trimmed) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-engraving-panel__pill button button-unstyled';
      button.setAttribute('data-engraving-pill', trimmed);
      button.setAttribute('aria-label', `Use example: ${trimmed}`);
      button.textContent = trimmed;
      chipsContainer.appendChild(button);
    });
  }

  #syncPreview() {
    const config = this.#getConfig();
    if (!config || !this.refs.panelInput || !this.refs.previewText || !this.refs.characterCount || !this.refs.saveButton) {
      return;
    }

    const maxChars = Number(config.maxChars || 3);
    const preview = config.preview || {};
    const rotation = preview.rotation != null ? preview.rotation : -10;
    const minScale = preview.minScale != null ? Number(preview.minScale) : 0.62;
    const scaleStep = preview.scaleStep != null ? Number(preview.scaleStep) : 0.08;
    const baseFontSize = preview.fontSize || 'clamp(10px, 1.8vw, 24px)';

    let value = sanitiseValue(this.refs.panelInput.value);
    value = value.slice(0, maxChars);

    if (this.refs.panelInput.value !== value) {
      this.refs.panelInput.value = value;
    }

    const displayValue = value || emptyPreviewSampleText(maxChars);
    const length = Math.max(value.length, 1);
    const scale = Math.max(minScale, 1 - (length - 1) * scaleStep);

    this.refs.previewText.style.fontSize = baseFontSize;
    this.refs.previewText.textContent = displayValue;
    this.refs.previewText.style.transform = `rotate(${rotation}deg) scale(${scale})`;
    this.refs.characterCount.textContent = `${value.length} / ${maxChars}`;
    this.refs.saveButton.disabled = !value;
  }

  #handleInput = (event) => {
    if (event.target !== this.refs.panelInput) return;
    this.#syncPreview();
  };

  #handleClick = (event) => {
    const pill = event.target.closest('[data-engraving-pill]');
    if (pill && this.contains(pill)) {
      const value = pill.getAttribute('data-engraving-pill') || '';
      if (this.refs.panelInput) {
        this.refs.panelInput.value = value;
        this.refs.panelInput.focus();
      }
      this.#syncPreview();
      return;
    }

    if (event.target === this.refs.editButton || this.refs.editButton?.contains(event.target)) {
      event.preventDefault();
      this.showDialog();
      return;
    }

    if (event.target === this.refs.removeButton || this.refs.removeButton?.contains(event.target)) {
      event.preventDefault();
      this.clearEngraving();
    }
  };

  /**
   * @param {number} amount
   */
  #formatMoney(amount) {
    const formatted = amount.toFixed(2);
    const shopify = window.Shopify;

    if (shopify?.formatMoney) {
      return shopify.formatMoney(amount * 100);
    }

    return `$${formatted}`;
  }
}

customElements.define('product-engraving-cta', ProductEngravingCta);
